import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  Tooltip
} from '@material-ui/core';
import RichEditor from '../../components/RichEditor/RichEditor';
import {
  BENEFITS,
  CATEGORIES_JOB,
  REGIONS_COUNTRIES,
  TYPES_JOB
} from '../../config';
import MultipleSelectChip from '../../components/MultipleSelectChip/MultipleSelectChip';
import {
  BENEFITS_JOB,
  CATEGORY_JOB,
  DETAILS_JOB,
  HOW_TO_APPLY_JOB,
  REGIONAL_RESTRICTIONS_JOB,
  TITLE_JOB,
  TYPE_JOB,
  INTERNAL_CANDIDATES_ENABLED_JOB
} from '../../common/adapters';
import { convertFromRaw, EditorState } from 'draft-js';
import PropTypes from 'prop-types';

const marginBottomSpacingInputs = 46;
const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiFormLabel-asterisk': {
      color: 'red'
    },
    '& .MuiSelect-outlined': {
      padding: '20px'
    },
    '& .MuiSelect-icon': {
      right: '7px'
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 3
    }
  },
  jobTypeTitle: {
    marginBottom: '12px !important'
  },
  textField: {
    marginLeft: 0,
    marginRight: 0,
    marginBottom: marginBottomSpacingInputs
  },
  formControl: {
    marginBottom: marginBottomSpacingInputs
  },
  textEditorTitle: {
    marginBottom: '24px !important',
    marginTop: 20
  },
  textEditorInputArea: {
    padding: 0,
    marginLeft: 0,
    marginBottom: 40
  },
  textEditorInputAreaError: {},
  headerCard: {
    textAlign: 'center'
  },
  inputName: {
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    marginBottom: 5
  },
  requiredText: {
    textAlign: 'right'
  },
  radioBtn: {
    transform: 'translate(0, 1.5px) scale(0.75)',
    transformOrigin: 'top left'
  },
  internalCandidatesSection: {
    marginTop: 20,
    marginBottom: 20,
    padding: 16,
    backgroundColor: theme.palette.background.default,
    borderRadius: 8
  },
  switchLabel: {
    marginBottom: 8
  }
}));

const JobForm = ({
  formValues,
  handleChange,
  handleChangeCustomFieldName,
  hasError,
  formErrors
}) => {
  const classes = useStyles();
  return (
    <>
      <Card className={classes.root}>
        <form autoComplete="off" noValidate>
          <CardContent>
            <CardHeader className={classes.headerCard} title="" />
            <Typography className={classes.inputName} variant="h5">
              Position
            </Typography>
            <TextField
              data-test="title-job"
              className={classes.textField}
              error={hasError(TITLE_JOB)}
              fullWidth
              helperText={
                hasError(TITLE_JOB)
                  ? formErrors[TITLE_JOB][0]
                  : 'Example: “Senior Designer”. Titles must describe one position.'
              }
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={TITLE_JOB}
              onChange={handleChange}
              required
              value={formValues ? formValues[TITLE_JOB] : ''}
              variant="outlined"
            />
            <Typography className={classes.inputName} variant="h5">
              Category
            </Typography>
            <TextField
              data-test="category-job"
              className={classes.textField}
              fullWidth
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="dense"
              name={CATEGORY_JOB}
              onChange={handleChange}
              // eslint-disable-next-line react/jsx-sort-props
              required
              select
              SelectProps={{ native: true }}
              value={formValues ? formValues[CATEGORY_JOB] : ''}
              variant="outlined">
              {CATEGORIES_JOB.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <Typography
              className={classes.inputName + ' ' + classes.jobTypeTitle}
              variant="h5">
              Job Type
            </Typography>
            <TextField
              data-test="type-job"
              className={classes.textField}
              fullWidth
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="dense"
              name={TYPE_JOB}
              onChange={handleChange}
              // eslint-disable-next-line react/jsx-sort-props
              required
              select
              SelectProps={{ native: true }}
              value={formValues ? formValues[TYPE_JOB] : ''}
              variant="outlined">
              {TYPES_JOB.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <Typography className={classes.inputName} variant="h5">
              JOB IS RESTRICTED TO LOCATIONS?
            </Typography>
            <MultipleSelectChip
              dataTestId="regional-restriction-job"
              defaultOptionsToUse={REGIONS_COUNTRIES}
              defaultSelectedValues={
                formValues && formValues[REGIONAL_RESTRICTIONS_JOB]
                  ? formValues[REGIONAL_RESTRICTIONS_JOB].split(',')
                  : ['🌏 Worldwide']
              }
              onChangeSelection={(items) => {
                handleChangeCustomFieldName(
                  items.length > 0 ? items.join(',') : '',
                  REGIONAL_RESTRICTIONS_JOB
                );
              }}
            />
            <Typography
              data-test="tite-detail-job"
              className={classes.inputName + ' ' + classes.textEditorTitle}
              variant="h5">
              Job Description
            </Typography>
            <Paper className={classes.textEditorInputArea}>
              <RichEditor
                name={DETAILS_JOB}
                onChange={(content) => {
                  const hasDetailsJob = content
                    ? EditorState.createWithContent(convertFromRaw(content))
                        .getCurrentContent()
                        .hasText()
                    : false;
                  handleChangeCustomFieldName(
                    hasDetailsJob ? content : null,
                    DETAILS_JOB
                  );
                }}
                placeholder="Give more details about the job..."
                value={formValues ? formValues[DETAILS_JOB] : null}
              />
              {hasError(DETAILS_JOB) && (
                <Typography
                  color="error"
                  variant="caption"
                  className={classes.textEditorInputAreaError}>
                  {formErrors[DETAILS_JOB][0]}
                </Typography>
              )}
            </Paper>
            <Typography className={classes.inputName} variant="h5">
              BENEFITS
            </Typography>
            <MultipleSelectChip
              dataTestId="benefits-job"
              defaultOptionsToUse={BENEFITS}
              defaultSelectedValues={
                formValues && formValues[BENEFITS_JOB]
                  ? formValues[BENEFITS_JOB].split(',')
                  : []
              }
              onChangeSelection={(items) => {
                handleChangeCustomFieldName(items.join(','), BENEFITS_JOB);
              }}
            />

            <Box className={classes.internalCandidatesSection}>
              <Typography className={classes.inputName} variant="h5">
                INTERNAL CANDIDATES
              </Typography>
              {(() => {
                const isInternalEnabled =
                  formValues?.[INTERNAL_CANDIDATES_ENABLED_JOB] !== false;
                const tooltipContent = isInternalEnabled ? (
                  <Box>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600, marginBottom: 6 }}>
                      <span role="img" aria-label="celebration">
                        🎉
                      </span>{' '}
                      Smart candidate management includes:
                    </Typography>
                    <Typography
                      variant="body2"
                      component="ul"
                      style={{ paddingLeft: 18, margin: 0 }}>
                      <li>
                        <span role="img" aria-label="sparkles">
                          ✨
                        </span>{' '}
                        Candidates apply directly through the platform
                      </li>
                      <li>
                        <span role="img" aria-label="chart">
                          📊
                        </span>{' '}
                        Manage all applications in one Kanban dashboard
                      </li>
                      <li>
                        <span role="img" aria-label="bell">
                          🔔
                        </span>{' '}
                        Instant notifications on new applications
                      </li>
                      <li>
                        <span role="img" aria-label="target">
                          🎯
                        </span>{' '}
                        Drag-and-drop status tracking
                      </li>
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600, marginBottom: 6 }}>
                      <span role="img" aria-label="warning">
                        ⚠️
                      </span>{' '}
                      Manual tracking means:
                    </Typography>
                    <Typography
                      variant="body2"
                      component="ul"
                      style={{ paddingLeft: 18, margin: 0 }}>
                      <li>
                        <span role="img" aria-label="email">
                          📧
                        </span>{' '}
                        Check email or external forms manually
                      </li>
                      <li>
                        <span role="img" aria-label="memo">
                          📝
                        </span>{' '}
                        Track candidates in spreadsheets
                      </li>
                      <li>
                        <span role="img" aria-label="cross mark">
                          ❌
                        </span>{' '}
                        No Kanban board or notifications
                      </li>
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ marginTop: 8, fontWeight: 600 }}>
                      <span role="img" aria-label="light bulb">
                        💡
                      </span>{' '}
                      We recommend keeping this enabled.
                    </Typography>
                  </Box>
                );

                return (
                  <FormControlLabel
                    className={classes.switchLabel}
                    control={
                      <Switch
                        checked={isInternalEnabled}
                        onChange={(e) => {
                          handleChangeCustomFieldName(
                            e.target.checked,
                            INTERNAL_CANDIDATES_ENABLED_JOB
                          );
                        }}
                        name={INTERNAL_CANDIDATES_ENABLED_JOB}
                        color="primary"
                      />
                    }
                    label={
                      <Tooltip title={tooltipContent} placement="top-start">
                        <Box>
                          <Typography variant="body1" style={{ fontWeight: 600 }}>
                            {isInternalEnabled ? (
                              <>
                                <span role="img" aria-label="check mark">
                                  ✅
                                </span>{' '}
                                Enabled - Smart Candidate Management
                              </>
                            ) : (
                              <>
                                <span role="img" aria-label="warning">
                                  ⚠️
                                </span>{' '}
                                Disabled - Manual Application Tracking
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Tooltip>
                    }
                  />
                );
              })()}
            </Box>

            {formValues?.[INTERNAL_CANDIDATES_ENABLED_JOB] === false && (
              <>
                <Typography className={classes.inputName} variant="h5">
                  How to Apply
                </Typography>
                <TextField
                  data-test="how-to-apply-job"
                  className={classes.textField}
                  error={hasError(HOW_TO_APPLY_JOB)}
                  fullWidth
                  helperText={
                    hasError(HOW_TO_APPLY_JOB)
                      ? formErrors[HOW_TO_APPLY_JOB][0]
                      : 'Link to application page or Email only (Required when Internal Candidates is disabled)'
                  }
                  InputLabelProps={{
                    shrink: true
                  }}
                  label=""
                  margin="normal"
                  name={HOW_TO_APPLY_JOB}
                  onChange={handleChange}
                  required
                  value={formValues ? formValues[HOW_TO_APPLY_JOB] : ''}
                  variant="outlined"
                />
              </>
            )}
          </CardContent>
        </form>
      </Card>
    </>
  );
};

JobForm.defaultProps = {
  formErrors: {},
  formValues: {},
  handleChange: () => {},
  handleChangeCustomFieldName: () => {},
  hasError: () => {}
};

JobForm.propTypes = {
  formErrors: PropTypes.object,
  formValues: PropTypes.object,
  handleChange: PropTypes.func,
  handleChangeCustomFieldName: PropTypes.func,
  hasError: PropTypes.func
};

export default JobForm;
